import { Facility } from '@reliance/types/models/Facility';
import { createColumnHelper } from '@tanstack/react-table';
import { FORM_ERROR } from 'final-form';
import React, { useState } from 'react';
import Button from 'react-bootstrap/Button';
import { Link } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import { useAlerts } from '../alert/AlertContext';
import api from '../common/api';
import { saveFacilityConfigurations } from '../hooks/Configuration';
import { useFacilitiesWithCache } from '../hooks/Facility';
import BaseHtmlTable from '../table/BaseHtmlTable';
import AddModal from './AddModal';

const Main: React.FC = () => {
  const facilities = useFacilitiesWithCache();
  const [showAddFacility, setShowAddFacility] = useState(false);
  const alerts = useAlerts();

  const columnHelper = createColumnHelper<Facility>();
  const columnDefs = [
    columnHelper.accessor((f) => f.id.toString(), {
      id: 'id',
      header: () => 'ID',
      filterFn: 'startsWithString'
    }),
    columnHelper.accessor('name', {
      header: () => 'Name'
    }),
    columnHelper.accessor('city', {
      header: () => 'City'
    }),
    columnHelper.accessor('state', {
      header: () => 'State',
      filterFn: 'startsWithString'
    }),
    columnHelper.accessor((f) => f.active ? 'Active' : 'Inactive', {
      id: 'active',
      header: () => 'Active'
    }),
    columnHelper.display({
      id: 'options',
      cell: (data) => (
        <Link
          className="btn btn-primary"
          to={`/facilities/view/${data.row.original.id}/details`}>
          View
        </Link>
      )
    })
  ];

  return (
    <Card className="mt-3">
      <Card.Header>
        <Button variant="primary" onClick={() => setShowAddFacility(true)}>
          <i className="fas fa-circle-plus" />&nbsp;Add Facility
        </Button>
      </Card.Header>
      <Card.Body>
        <BaseHtmlTable columnDefs={columnDefs} rows={facilities.result || []} />
        {showAddFacility && (
          <AddModal
            onClose={() => setShowAddFacility(false)}
            onSave={async (formData, pendingFacilityConfigurations) => {
              try {
                const { data } = await api.post<{ id?: number }>('/facility', formData);
                const facilityId = data?.id;
                const pendingKeys = Object.keys(pendingFacilityConfigurations);
                if (facilityId != null && pendingKeys.length > 0) {
                  const configurationKeys = pendingKeys.map((key) => ({ key }));
                  const configError = await saveFacilityConfigurations(
                    facilityId,
                    configurationKeys,
                    [],
                    pendingFacilityConfigurations
                  );
                  if (configError != null) {
                    return configError;
                  }
                }
                alerts.success('Facility was successfully added');
                setShowAddFacility(false);
                await facilities.execute({ force: true });
                return undefined;
              } catch (err) {
                return { [FORM_ERROR]: err };
              }
            }}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default Main;
